import { ApiError } from '@app/api/core/ApiError';
import { getUiErrorKind, UiErrorKind } from '@app/utils/handleApiError';
import {
  ApiResponseErrorDetail,
  BaseResponse,
} from '@app/modules/mje/api/accountingApi.types';

export interface MjeRequestFeedback {
  kind: 'noResults' | 'error';
  uiKind?: UiErrorKind;
  backendMessage?: string;
  statusCode?: number;
  exceptionCode?: number;
  reasonKey?: string;
}

export type MjeEnvelope<T> = T | BaseResponse<T | Record<string, unknown>>;

export const isMjeObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

export const isMjeEmptyObject = (
  value: unknown
): value is Record<string, unknown> =>
  isMjeObject(value) && Object.keys(value).length === 0;

export const getMjePayloadCode = (payload: unknown): number | undefined =>
  isMjeObject(payload) && typeof payload.code === 'number'
    ? payload.code
    : undefined;

export const getMjePayloadData = (payload: unknown): unknown =>
  isMjeObject(payload) && 'data' in payload ? payload.data : undefined;

export const getMjePayloadError = (
  payload: unknown
): ApiResponseErrorDetail | undefined => {
  if (!isMjeObject(payload) || !isMjeObject(payload.error)) {
    return undefined;
  }

  return payload.error as ApiResponseErrorDetail;
};

export const isMjeNoResultsPayload = (payload: unknown): boolean => {
  const code = getMjePayloadCode(payload);
  const data = getMjePayloadData(payload);
  const error = getMjePayloadError(payload);
  const message = String(error?.message ?? '').toLowerCase();
  const reasonKey = String(error?.reasonKey ?? '').toLowerCase();

  const explicitNoResults =
    message.includes('results not found') || reasonKey === 'resultsnotfound';

  if (Array.isArray(data)) {
    return code === 200 && data.length === 0;
  }

  return code === 200 && (explicitNoResults || isMjeEmptyObject(data));
};

export const parseMjeListResponse = <T>(payload: unknown) => {
  if (Array.isArray(payload)) {
    return {
      data: payload as T[],
      noResults: payload.length === 0,
    };
  }

  const data = getMjePayloadData(payload);

  if (Array.isArray(data)) {
    return {
      data: data as T[],
      noResults: isMjeNoResultsPayload(payload) || data.length === 0,
    };
  }

  return {
    data: [] as T[],
    noResults: isMjeNoResultsPayload(payload),
  };
};

export const parseMjeValueResponse = <T>(payload: unknown) => {
  if (Array.isArray(payload)) {
    return {
      data: payload as unknown as T,
      noResults: payload.length === 0,
    };
  }

  if (isMjeNoResultsPayload(payload)) {
    return {
      data: undefined,
      noResults: true,
    };
  }

  const data = getMjePayloadData(payload);

  if (data === undefined) {
    return {
      data: (isMjeObject(payload) ? undefined : (payload as T)) ?? undefined,
      noResults: false,
    };
  }

  if (isMjeEmptyObject(data)) {
    return {
      data: undefined,
      noResults: false,
    };
  }

  return {
    data: data as T,
    noResults: false,
  };
};

export const extractMjeData = <T>(payload: unknown): T | undefined =>
  parseMjeValueResponse<T>(payload).data;

export const getMjeErrorPayload = (error: unknown): unknown => {
  if (error instanceof ApiError) {
    return error.options;
  }

  if (
    isMjeObject(error) &&
    isMjeObject(error.response) &&
    'data' in error.response
  ) {
    return error.response.data;
  }

  return undefined;
};

export const buildMjeNoResultsFeedback = (
  payload?: unknown
): MjeRequestFeedback => {
  const error = getMjePayloadError(payload);

  return {
    kind: 'noResults',
    backendMessage: error?.message,
    statusCode: getMjePayloadCode(payload) ?? 200,
    exceptionCode: error?.exceptionCode,
    reasonKey: error?.reasonKey,
  };
};

export const buildMjeErrorFeedback = (error: unknown): MjeRequestFeedback => {
  const payload = getMjeErrorPayload(error);
  const payloadError = getMjePayloadError(payload);
  const apiError = error instanceof ApiError ? error : undefined;

  return {
    kind: 'error',
    uiKind: getUiErrorKind(error),
    backendMessage:
      payloadError?.message ||
      apiError?.message ||
      (error instanceof Error ? error.message : undefined),
    statusCode: getMjePayloadCode(payload) ?? apiError?.status,
    exceptionCode: payloadError?.exceptionCode,
    reasonKey: payloadError?.reasonKey,
  };
};

export const logMjeRequestError = (
  scope: string,
  error: unknown,
  feedback: MjeRequestFeedback
) => {
  console.error(`[MJE][${scope}] Request failed`, {
    feedback,
    payload: getMjeErrorPayload(error),
    error,
  });
};
